/**
 * lib/transcript.ts
 * ------------------------------------------------------------------
 * Builds a per-term-per-subject grid for one student across every
 * term that has marks data for them. This is the *data* layer for
 * the Student Profile transcript — kept pure so the math is unit-
 * testable independent of any PDF or React rendering.
 *
 * Honest scope: this aggregates the same `reportMarks` collection
 * used everywhere else, and applies the SAME `deriveMark` engine,
 * so the transcript can never disagree with what a student saw on
 * their term report. No new mark logic, no shadow path.
 *
 * Honest limitation: a real transcript only "shines" once a student
 * has multiple terms of data. For a student with only Term 1 data,
 * the grid has one column and the rest are empty. That's correct
 * behaviour, not a bug — just don't be surprised by it.
 *
 * MOST RECENT RESULTS (Aug 2026). The per-subject view used to read the
 * newest column and nothing else, so a subject with no mark THIS term
 * showed a dash — a pupil who dropped it, was absent for the exam, or whose
 * class had not finished marking looked as though they had never sat it.
 * `latestBySubject` reaches back through earlier terms for the last result
 * the pupil actually attained, and carries the term it came from so the
 * screen can say so. A figure shown without its term is worse than the
 * dash: it reads as this term's mark.
 * ------------------------------------------------------------------
 */
import type {
  Subject,
  SubjectMarkEntry,
  MarksDocument,
} from '@/types';
import { deriveMark, schemeForSubject } from './grading';
import type { TermKey } from './school.config';

export interface TranscriptCell {
  /** Numeric mark used for grading; null when ABS / not entered. */
  mark: number | null;
  /** Letter grade; '-' when no usable mark. */
  grade: string;
}

export interface TranscriptTerm {
  year: string;
  term: TermKey;
  /** Label like "2026 · Term 1" — what the column header shows. */
  label: string;
  /**
   * Per-subject cell, keyed by subjectId. Subjects the student
   * didn't take in this term are simply absent — the row will
   * show '-' for those columns.
   */
  cells: Record<string, TranscriptCell>;
  /** Average of usable subject marks this term, null if none. */
  termAverage: number | null;
}

/**
 * The most recent result a student actually ATTAINED in a subject.
 *
 * Not the same as "their mark in the latest term". A pupil who dropped
 * Geography after Term 1, or was absent for an exam, or whose class had not
 * finished marking, has no cell in the newest column — and the screen showed
 * them a dash, as though they had never sat the subject at all. The mark is
 * on file; it is simply one column further left.
 *
 * So this carries the mark AND the term it came from. Showing the figure
 * without saying which term it belongs to would be worse than the dash: a
 * parent would read a Term 1 result as this term's.
 */
export interface LatestResult extends TranscriptCell {
  year: string;
  term: TermKey;
  /** "2026 · Term 1" — shown beside the mark when it is not the newest term. */
  label: string;
  /** True when this came from the most recent term on the transcript. */
  isCurrentTerm: boolean;
}

export interface TranscriptGrid {
  /**
   * Subjects to show as rows — the union of subjects the student
   * has any mark for, across all included terms. Ordered by
   * subject.displayOrder then name, matching the rest of the app.
   */
  subjects: Subject[];
  /** One column per (year, term) we have data for, oldest first. */
  terms: TranscriptTerm[];
  /** Cumulative average across all terms with data, null if none. */
  overallAverage: number | null;
  /**
   * Per subject, the most recent result the pupil attained — reaching back
   * through earlier terms when the newest one has nothing for them.
   * Keyed by subjectId; a subject with no usable mark in ANY term is absent.
   */
  latestBySubject: Record<string, LatestResult>;
  /** The newest term with any data, or null. */
  latestTerm: TranscriptTerm | null;
}

export interface BuildTranscriptInput {
  studentId: string;
  /** All marks docs in the system (caller queries Firestore once). */
  marksDocs: MarksDocument[];
  /** Subject lookup so rows can have names + displayOrder. */
  subjects: Subject[];
}

/**
 * Build the grid. Pure function: same input -> same output, no IO.
 * Re-running with a superset of marksDocs widens the grid; never
 * mutates anything.
 */
export function buildTranscript(
  input: BuildTranscriptInput,
): TranscriptGrid {
  const { studentId, marksDocs, subjects } = input;

  // group marks docs by (year, term), keeping only those that
  // actually contain an entry for THIS student
  const byTerm = new Map<
    string,
    { year: string; term: TermKey; docs: MarksDocument[] }
  >();
  for (const md of marksDocs) {
    const e = (md.entries as Record<string, SubjectMarkEntry>)?.[
      studentId
    ];
    if (!e) continue;
    const key = `${md.year}__${md.term}`;
    const slot = byTerm.get(key) ?? {
      year: md.year,
      term: md.term as TermKey,
      docs: [],
    };
    slot.docs.push(md);
    byTerm.set(key, slot);
  }

  // ordered terms: by year (lexical = numeric for "2026" etc),
  // then Term1 < Term2 < Term3
  const termOrder: Record<string, number> = {
    Term1: 1,
    Term2: 2,
    Term3: 3,
  };
  const terms: TranscriptTerm[] = [...byTerm.values()]
    .sort(
      (a, b) =>
        a.year.localeCompare(b.year) ||
        (termOrder[a.term] ?? 99) - (termOrder[b.term] ?? 99),
    )
    .map((slot) => {
      const cells: Record<string, TranscriptCell> = {};
      const marksForAvg: number[] = [];
      for (const md of slot.docs) {
        const e = (md.entries as Record<string, SubjectMarkEntry>)[
          studentId
        ];
        if (!e) continue;
        // Re-derive using the canonical engine so the transcript
        // never diverges from the report card, even if the stored
        // termMark/reportGrade was written by an older code path.
        const d = deriveMark(
          slot.term,
          e.ca ?? {},
          e.exam,
          schemeForSubject(subjects.find((s) => s.id === md.subjectId)),
        );
        const grade =
          d.reportGrade === null || d.reportGrade === ''
            ? '-'
            : d.reportGrade;
        // We show termMark on the grid (the average / final mark
        // the report displays); falling back to gradeSource only
        // when termMark was never computed.
        const numericRaw =
          d.termMark ??
          (typeof d.gradeSource === 'number'
            ? d.gradeSource
            : null);
        cells[md.subjectId] = { mark: numericRaw, grade };
        if (typeof numericRaw === 'number')
          marksForAvg.push(numericRaw);
      }
      const termAverage =
        marksForAvg.length > 0
          ? Math.round(
              marksForAvg.reduce((a, b) => a + b, 0) /
                marksForAvg.length,
            )
          : null;
      return {
        year: slot.year,
        term: slot.term,
        label: `${slot.year} · ${slot.term.replace('Term', 'Term ')}`,
        cells,
        termAverage,
      };
    });

  // subjects = union of subject ids that appear in any term cell
  const seen = new Set<string>();
  for (const t of terms) for (const sid of Object.keys(t.cells)) seen.add(sid);
  const subjectRows: Subject[] = subjects
    .filter((s) => seen.has(s.id))
    .sort(
      (a, b) =>
        (a.displayOrder ?? 999) - (b.displayOrder ?? 999) ||
        a.name.localeCompare(b.name),
    );

  // overall: average of every term average (each term equally weighted)
  const termAverages = terms
    .map((t) => t.termAverage)
    .filter((v): v is number => v !== null);
  const overallAverage =
    termAverages.length > 0
      ? Math.round(
          termAverages.reduce((a, b) => a + b, 0) /
            termAverages.length,
        )
      : null;

  /* MOST RECENT RESULT PER SUBJECT.
     Walk the terms newest-first and take the first usable mark for each
     subject. `terms` is oldest-first, so this reads backwards; the first hit
     is by construction the latest one the pupil attained. A cell with a null
     mark does not count as attained — it is an absence or an un-entered
     mark, and treating it as a result would overwrite a real earlier one
     with a dash. */
  const latestTerm = terms.length > 0 ? terms[terms.length - 1] : null;
  const latestBySubject: Record<string, LatestResult> = {};
  for (let i = terms.length - 1; i >= 0; i--) {
    const t = terms[i];
    for (const [sid, cell] of Object.entries(t.cells)) {
      if (latestBySubject[sid]) continue;
      if (cell.mark === null && cell.grade === '-') continue;
      latestBySubject[sid] = {
        ...cell,
        year: t.year,
        term: t.term,
        label: t.label,
        isCurrentTerm: latestTerm !== null && t.year === latestTerm.year && t.term === latestTerm.term,
      };
    }
  }

  return { subjects: subjectRows, terms, overallAverage, latestBySubject, latestTerm };
}
